import { useState, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { AppLayout, GlassCard, PageHeader, LoadingSpinner } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { User, Save, Camera, Shield, Mail, MapPin, GraduationCap, Target, Wrench, BookOpen, Heart, DollarSign, Monitor } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import PatriciaPageGuide from '../components/PatriciaPageGuide';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const US_STATES_LIST = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','DC','FL','GA','HI','ID','IL','IN','IA',
  'KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY',
  'NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY'
];

const INTEREST_OPTIONS = [
  "Computer Science","Engineering","Business","Mathematics","Biology","Chemistry","Physics",
  "Medicine","Nursing","Law","Education","Art","Music","Theater","Writing","Film",
  "Psychology","Political Science","Environmental Science","Robotics","Data Science",
  "Architecture","Journalism","Economics","Finance","Marketing","History","Philosophy",
  "Sociology","Foreign Languages","Graphic Design","Culinary Arts","Criminal Justice",
  "Agriculture","Construction","Automotive","HVAC","Welding","Electrician"
];

export default function ProfilePage() {
  const { user, getAuthHeader, updateUser } = useAuth();
  const [saving, setSaving] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const fileInputRef = useRef(null);

  const isParent = user?.role === 'parent';
  const isProfessional = user?.role === 'professional';
  const isStudent = user?.role === 'student';

  const [formData, setFormData] = useState({
    full_name: user?.full_name || '',
    bio: user?.bio || '',
    state: user?.state || '',
    city: user?.city || '',
    zip_code: user?.zip_code || '',
    grade_level: user?.grade_level || user?.grade || '',
    gpa: user?.gpa || '',
    major: user?.major || '',
    institution: user?.institution || '',
    academic_year: user?.academic_year || '',
    credits_earned: user?.credits_earned ?? '',
    credits_required: user?.credits_required ?? '',
    expected_graduation: user?.expected_graduation || '',
    interests: user?.interests || [],
    career_aspirations: user?.career_aspirations || '',
    skills: user?.skills || [],
    extracurriculars: user?.extracurriculars || [],
    school_type_goal: user?.school_type_goal || '',
    pathway_preference: user?.pathway_preference || '',
    first_generation: user?.first_generation || false,
    income_sensitive: user?.income_sensitive || false,
    preferred_format: user?.preferred_format || '',
  });

  const [interestInput, setInterestInput] = useState('');
  const [skillInput, setSkillInput] = useState('');
  const [extraInput, setExtraInput] = useState('');

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Please select an image file'); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error('Image must be less than 5MB'); return; }
    setUploadingPhoto(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const response = await axios.post(`${API}/upload/profile-photo`, fd, {
        ...getAuthHeader(), headers: { ...getAuthHeader().headers, 'Content-Type': 'multipart/form-data' }
      });
      if (response.data?.url) { updateUser({ ...user, picture: response.data.url }); toast.success('Photo updated!'); }
    } catch { toast.error('Failed to upload photo'); }
    finally { setUploadingPhoto(false); }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        full_name: formData.full_name || undefined,
        bio: formData.bio || undefined,
        state: formData.state || undefined,
        city: formData.city || undefined,
        zip_code: formData.zip_code || undefined,
        interests: formData.interests.length > 0 ? formData.interests : undefined,
        career_aspirations: formData.career_aspirations || undefined,
        skills: formData.skills.length > 0 ? formData.skills : undefined,
        extracurriculars: formData.extracurriculars.length > 0 ? formData.extracurriculars : undefined,
        school_type_goal: formData.school_type_goal || undefined,
        pathway_preference: formData.pathway_preference || undefined,
        first_generation: formData.first_generation,
        income_sensitive: formData.income_sensitive,
        preferred_format: formData.preferred_format || undefined,
      };
      if (formData.grade_level) payload.grade_level = String(formData.grade_level);
      if (formData.gpa) payload.gpa = parseFloat(formData.gpa);
      payload.major = formData.major || undefined;
      payload.institution = formData.institution || undefined;
      payload.academic_year = formData.academic_year || undefined;
      payload.expected_graduation = formData.expected_graduation || undefined;
      if (formData.credits_earned !== '') payload.credits_earned = parseInt(formData.credits_earned, 10);
      if (formData.credits_required !== '') payload.credits_required = parseInt(formData.credits_required, 10);

      const response = await axios.put(`${API}/profile`, payload, getAuthHeader());
      updateUser(response.data || { ...user, ...payload });
      toast.success('Profile saved! Your matches will now be more accurate.');
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const addToList = (field, value, setInput) => {
    if (!value.trim()) return;
    const current = formData[field] || [];
    if (!current.includes(value.trim())) {
      setFormData({ ...formData, [field]: [...current, value.trim()] });
    }
    setInput('');
  };

  const removeFromList = (field, idx) => {
    const current = [...(formData[field] || [])];
    current.splice(idx, 1);
    setFormData({ ...formData, [field]: current });
  };

  return (
    <AppLayout>
      <PageHeader title="Settings" description="Manage your profile, complete profile = better scholarship & program matches"
        action={<Button onClick={handleSave} disabled={saving} className="btn-primary" data-testid="save-profile">
          {saving ? <LoadingSpinner size="sm" /> : <Save className="w-5 h-5 mr-2" />} Save Changes
        </Button>} />
      <div className="mb-4"><PatriciaPageGuide pageId="profile" /></div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Basic Info */}
          <GlassCard>
            <h2 className="font-outfit text-lg font-semibold text-white mb-6 flex items-center gap-2"><User className="w-5 h-5 text-blue-400" /> Profile</h2>
            <div className="flex items-center gap-6 mb-6 pb-6 border-b border-slate-700">
              <div className="relative">
                <div className="w-20 h-20 rounded-full overflow-hidden bg-slate-800 flex items-center justify-center border-2 border-slate-700">
                  {user?.picture ? <img src={user.picture} alt="Profile" className="w-full h-full object-cover" /> : <User className="w-8 h-8 text-slate-300" />}
                </div>
                <button onClick={() => fileInputRef.current?.click()} disabled={uploadingPhoto}
                  className="absolute bottom-0 right-0 w-7 h-7 bg-blue-600 hover:bg-blue-700 rounded-full flex items-center justify-center text-white transition-colors" data-testid="upload-photo-btn">
                  {uploadingPhoto ? <LoadingSpinner size="sm" /> : <Camera className="w-3.5 h-3.5" />}
                </button>
                <input ref={fileInputRef} type="file" accept="image/*" onChange={handlePhotoUpload} className="hidden" />
              </div>
              <div><h3 className="text-white font-medium">{user?.full_name || 'Your Name'}</h3><p className="text-slate-300 text-sm">{user?.email}</p></div>
            </div>
            <div className="grid md:grid-cols-2 gap-5">
              <div className="space-y-2">
                <Label className="text-slate-300">Full Name</Label>
                <Input value={formData.full_name} onChange={(e) => setFormData({ ...formData, full_name: e.target.value })} className="input-dark" data-testid="profile-name" />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Email</Label>
                <Input value={user?.email || ''} disabled className="input-dark opacity-60" />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">State</Label>
                <select value={formData.state} onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                  className="w-full bg-slate-800 border border-slate-700 text-white text-sm rounded-lg p-2.5" data-testid="profile-state">
                  <option value="">Select your state</option>
                  {US_STATES_LIST.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">City</Label>
                <Input placeholder="Your city" value={formData.city} onChange={(e) => setFormData({ ...formData, city: e.target.value })} className="input-dark" data-testid="profile-city" />
              </div>
            </div>
            <div className="mt-5 space-y-2">
              <Label className="text-slate-300">About</Label>
              <Textarea placeholder={isParent ? "Tell us about your family goals..." : isProfessional ? "Describe your career transition goals..." : "Tell us about yourself..."}
                value={formData.bio} onChange={(e) => setFormData({ ...formData, bio: e.target.value })} className="input-dark min-h-[80px]" data-testid="profile-bio" />
            </div>
          </GlassCard>

          {/* Academic & Matching Section (all roles benefit) */}
          <GlassCard>
            <h2 className="font-outfit text-lg font-semibold text-white mb-6 flex items-center gap-2"><GraduationCap className="w-5 h-5 text-purple-400" /> Academic & Matching Profile</h2>
            <p className="text-slate-300 text-xs mb-4">These fields improve your scholarship, program, and career matches.</p>
            <div className="grid md:grid-cols-2 gap-5">
              {!isParent && (
                <div className="space-y-2">
                  <Label className="text-slate-300">Grade Level</Label>
                  <select value={formData.grade_level} onChange={(e) => setFormData({ ...formData, grade_level: e.target.value })}
                    className="w-full bg-slate-800 border border-slate-700 text-white text-sm rounded-lg p-2.5" data-testid="profile-grade">
                    <option value="">Select</option>
                    <option value="9th">9th Grade</option><option value="10th">10th Grade</option>
                    <option value="11th">11th Grade</option><option value="12th">12th Grade</option>
                    <option value="freshman">College Freshman</option><option value="sophomore">College Sophomore</option>
                    <option value="junior">College Junior</option><option value="senior">College Senior</option>
                    <option value="graduate">Graduate Student</option>
                  </select>
                </div>
              )}
              {!isParent && (
                <div className="space-y-2">
                  <Label className="text-slate-300">GPA</Label>
                  <Input type="number" step="0.01" min="0" max="4.0" placeholder="e.g. 3.5"
                    value={formData.gpa} onChange={(e) => setFormData({ ...formData, gpa: e.target.value })} className="input-dark" data-testid="profile-gpa" />
                </div>
              )}
              {isStudent && (
                <>
                  <div className="space-y-2">
                    <Label className="text-slate-300">Major</Label>
                    <Input placeholder="e.g. Psychology" value={formData.major}
                      onChange={(e) => setFormData({ ...formData, major: e.target.value })} className="input-dark" data-testid="profile-major" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-300">Institution</Label>
                    <Input placeholder="e.g. Rutgers University-Camden" value={formData.institution}
                      onChange={(e) => setFormData({ ...formData, institution: e.target.value })} className="input-dark" data-testid="profile-institution" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-300">Academic Year</Label>
                    <select value={formData.academic_year} onChange={(e) => setFormData({ ...formData, academic_year: e.target.value })}
                      className="w-full bg-slate-800 border border-slate-700 text-white text-sm rounded-lg p-2.5" data-testid="profile-academic-year">
                      <option value="">Select</option>
                      <option value="freshman">Freshman</option><option value="sophomore">Sophomore</option>
                      <option value="junior">Junior</option><option value="senior">Senior</option>
                      <option value="graduate">Graduate</option>
                    </select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-300">Expected Graduation</Label>
                    <Input type="date" value={formData.expected_graduation}
                      onChange={(e) => setFormData({ ...formData, expected_graduation: e.target.value })} className="input-dark" data-testid="profile-graduation" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-300">Credits Earned</Label>
                    <Input type="number" min="0" placeholder="e.g. 72" value={formData.credits_earned}
                      onChange={(e) => setFormData({ ...formData, credits_earned: e.target.value })} className="input-dark" data-testid="profile-credits-earned" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-300">Credits Required</Label>
                    <Input type="number" min="0" placeholder="e.g. 120" value={formData.credits_required}
                      onChange={(e) => setFormData({ ...formData, credits_required: e.target.value })} className="input-dark" data-testid="profile-credits-required" />
                  </div>
                </>
              )}
              <div className="space-y-2">
                <Label className="text-slate-300">Career Goal</Label>
                <Input placeholder={isProfessional ? "e.g. Product Management" : "e.g. Software Engineer, Nurse"}
                  value={formData.career_aspirations} onChange={(e) => setFormData({ ...formData, career_aspirations: e.target.value })} className="input-dark" data-testid="profile-career" />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">School Type Goal</Label>
                <select value={formData.school_type_goal} onChange={(e) => setFormData({ ...formData, school_type_goal: e.target.value })}
                  className="w-full bg-slate-800 border border-slate-700 text-white text-sm rounded-lg p-2.5" data-testid="profile-school-type">
                  <option value="">Select</option>
                  <option value="4-year college">4-Year College</option><option value="community college">Community College</option>
                  <option value="trade school">Trade / Vocational</option><option value="graduate school">Graduate School</option>
                  <option value="certification">Certification Program</option><option value="military">Military</option>
                </select>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Preferred Format</Label>
                <select value={formData.preferred_format} onChange={(e) => setFormData({ ...formData, preferred_format: e.target.value })}
                  className="w-full bg-slate-800 border border-slate-700 text-white text-sm rounded-lg p-2.5" data-testid="profile-format">
                  <option value="">No preference</option><option value="in_person">In-Person</option>
                  <option value="virtual">Virtual / Online</option><option value="hybrid">Hybrid</option>
                </select>
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-5 mt-5">
              <label className="flex items-center gap-3 p-3 bg-slate-800/40 rounded-lg cursor-pointer">
                <input type="checkbox" checked={formData.first_generation} onChange={(e) => setFormData({ ...formData, first_generation: e.target.checked })}
                  className="rounded bg-slate-800 border-slate-600" data-testid="profile-first-gen" />
                <span className="text-slate-300 text-sm">First-generation college student</span>
              </label>
              <label className="flex items-center gap-3 p-3 bg-slate-800/40 rounded-lg cursor-pointer">
                <input type="checkbox" checked={formData.income_sensitive} onChange={(e) => setFormData({ ...formData, income_sensitive: e.target.checked })}
                  className="rounded bg-slate-800 border-slate-600" data-testid="profile-income" />
                <span className="text-slate-300 text-sm">Need-based financial aid relevant</span>
              </label>
            </div>
          </GlassCard>

          {/* Interests, Skills, Extracurriculars */}
          <GlassCard>
            <h2 className="font-outfit text-lg font-semibold text-white mb-6 flex items-center gap-2"><Target className="w-5 h-5 text-cyan-400" /> Interests, Skills & Activities</h2>
            {/* Interests */}
            <div className="mb-5">
              <Label className="text-slate-300 mb-2 block">Interests / Fields of Study</Label>
              <div className="flex gap-2 mb-2">
                <select value={interestInput} onChange={(e) => setInterestInput(e.target.value)}
                  className="flex-1 bg-slate-800 border border-slate-700 text-white text-sm rounded-lg p-2" data-testid="interest-select">
                  <option value="">Select an interest...</option>
                  {INTEREST_OPTIONS.filter(i => !formData.interests.includes(i)).map(i => <option key={i} value={i}>{i}</option>)}
                </select>
                <Button type="button" size="sm" onClick={() => addToList('interests', interestInput, setInterestInput)} className="btn-primary text-xs">Add</Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {formData.interests.map((int, i) => (
                  <span key={i} className="px-3 py-1 bg-blue-500/15 text-blue-300 rounded-full text-xs flex items-center gap-1">
                    {int} <button onClick={() => removeFromList('interests', i)} className="ml-1 text-blue-400 hover:text-red-400">&times;</button>
                  </span>
                ))}
              </div>
            </div>
            {/* Skills */}
            <div className="mb-5">
              <Label className="text-slate-300 mb-2 block">Skills</Label>
              <div className="flex gap-2 mb-2">
                <Input placeholder="Add a skill (e.g. Python, Leadership, Public Speaking)" value={skillInput}
                  onChange={(e) => setSkillInput(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addToList('skills', skillInput, setSkillInput))}
                  className="input-dark" data-testid="skill-input" />
                <Button type="button" size="sm" onClick={() => addToList('skills', skillInput, setSkillInput)} className="btn-primary text-xs">Add</Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {formData.skills.map((s, i) => (
                  <span key={i} className="px-3 py-1 bg-cyan-500/15 text-cyan-300 rounded-full text-xs flex items-center gap-1">
                    {s} <button onClick={() => removeFromList('skills', i)} className="ml-1 text-cyan-400 hover:text-red-400">&times;</button>
                  </span>
                ))}
              </div>
            </div>
            {/* Extracurriculars */}
            <div>
              <Label className="text-slate-300 mb-2 block">Extracurriculars & Activities</Label>
              <div className="flex gap-2 mb-2">
                <Input placeholder="Add activity (e.g. Basketball, Student Council, Debate)" value={extraInput}
                  onChange={(e) => setExtraInput(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addToList('extracurriculars', extraInput, setExtraInput))}
                  className="input-dark" data-testid="extra-input" />
                <Button type="button" size="sm" onClick={() => addToList('extracurriculars', extraInput, setExtraInput)} className="btn-primary text-xs">Add</Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {formData.extracurriculars.map((e, i) => (
                  <span key={i} className="px-3 py-1 bg-purple-500/15 text-purple-300 rounded-full text-xs flex items-center gap-1">
                    {e} <button onClick={() => removeFromList('extracurriculars', i)} className="ml-1 text-purple-400 hover:text-red-400">&times;</button>
                  </span>
                ))}
              </div>
            </div>
          </GlassCard>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          <GlassCard>
            <div className="flex flex-col items-center text-center">
              <div className="w-16 h-16 bg-slate-800 rounded-full flex items-center justify-center mb-3">
                {user?.picture ? <img src={user.picture} alt="" className="w-full h-full rounded-full object-cover" /> : <User className="w-8 h-8 text-slate-300" />}
              </div>
              <h3 className="font-outfit text-lg font-semibold text-white">{user?.full_name}</h3>
              <p className="text-slate-300 text-sm">{user?.email}</p>
              <div className="flex items-center gap-2 mt-3">
                <span className="px-3 py-1 bg-blue-500/20 text-blue-400 rounded-full text-xs capitalize">{user?.role?.replace('_', ' ')}</span>
              </div>
            </div>
          </GlassCard>
          <GlassCard>
            <h3 className="text-white font-semibold mb-3">Account Details</h3>
            <div className="space-y-3 text-sm">
              <div className="flex items-center gap-3 text-slate-300"><Mail className="w-4 h-4 flex-shrink-0" /><span className="truncate">{user?.email}</span></div>
              <div className="flex items-center gap-3 text-slate-300"><Shield className="w-4 h-4 flex-shrink-0" /><span className="capitalize">{user?.subscription_tier?.replace('_', ' ') || 'Free'} Plan</span></div>
              {formData.state && <div className="flex items-center gap-3 text-slate-300"><MapPin className="w-4 h-4 flex-shrink-0" /><span>{formData.city ? `${formData.city}, ` : ''}{formData.state}</span></div>}
              {formData.gpa && <div className="flex items-center gap-3 text-slate-300"><GraduationCap className="w-4 h-4 flex-shrink-0" /><span>GPA: {formData.gpa}</span></div>}
            </div>
          </GlassCard>
          <GlassCard className="bg-gradient-to-br from-blue-600/10 to-purple-600/10 border-blue-500/20">
            <h3 className="text-blue-300 font-semibold mb-2 text-sm">Better Profile = Better Matches</h3>
            <p className="text-slate-300 text-xs leading-relaxed">Fill in your state, GPA, interests, and career goals to get more accurate scholarship and program recommendations.</p>
          </GlassCard>
        </div>
      </div>
    </AppLayout>
  );
}
